// CD: backend
// One controller for cards on home, search, collections and dynamically inserted recommendations.
const selector = '.pushe-card__img--hover';
const states = new WeakMap();
const hover = window.matchMedia('(hover:hover) and (pointer:fine)');

function prepare(image) {
  const state = states.get(image);
  if (!state || state.pending || state.ready) return;
  state.pending = true;
  image.loading = 'eager';
  if (image.complete) reveal(image, state);
}

async function reveal(image, state) {
  if (!image.complete || !image.naturalWidth || state.decoding || state.ready) return;
  state.decoding = true;
  try {
    // No decode support means keep the safe primary image.
    await image.decode();
    if (states.get(image) !== state || !image.isConnected || !image.naturalWidth) return;
    state.ready = true;
    image.closest('.pushe-card')?.setAttribute('data-pushe-hover-ready', '');
    nearby?.unobserve(image);
  } catch { /* Failed loads/decodes never hide the primary image. */ }
  finally { state.decoding = false; state.pending = false; }
}

const nearby = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
  if (hover.matches) for (const entry of entries) if (entry.isIntersecting) prepare(entry.target);
}, { rootMargin: '200px' }) : null;

function register(image) {
  if (states.has(image)) {
    if (!states.get(image).ready) nearby?.observe(image);
    return;
  }
  states.set(image, {});
  image.closest('.pushe-card')?.removeAttribute('data-pushe-hover-ready');
  image.addEventListener('load', () => reveal(image, states.get(image)));
  image.addEventListener('error', () => {
    states.set(image, {});
    image.closest('.pushe-card')?.removeAttribute('data-pushe-hover-ready');
  });
  nearby?.observe(image);
  if (image.complete && image.naturalWidth) reveal(image, states.get(image));
}

function scan(root) {
  if (root.matches?.(selector)) register(root);
  root.querySelectorAll?.(selector).forEach(register);
}

function intent(event) {
  if (event.type === 'pointerover' && (event.pointerType === 'touch' || !hover.matches)) return;
  const image = event.target.closest?.('.pushe-card')?.querySelector(selector);
  if (image) { register(image); prepare(image); }
}

scan(document);
document.addEventListener('pointerover', intent);
document.addEventListener('focusin', intent);
new MutationObserver(records => {
  for (const record of records) {
    if (record.type === 'attributes') {
      const image = record.target;
      if (!image.matches(selector)) continue;
      image.closest('.pushe-card')?.removeAttribute('data-pushe-hover-ready');
      states.set(image, {});
      nearby?.observe(image);
      if (image.loading === 'eager') prepare(image);
    } else {
      for (const node of record.removedNodes) {
        if (node.matches?.(selector)) nearby?.unobserve(node);
        node.querySelectorAll?.(selector).forEach(image => nearby?.unobserve(image));
      }
      for (const node of record.addedNodes) scan(node);
      const card = record.target.closest?.('.pushe-card');
      if (card && !card.querySelector(selector)) card.removeAttribute('data-pushe-hover-ready');
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });
hover.addEventListener('change', () => {
  if (hover.matches) document.querySelectorAll(selector).forEach(image => { nearby?.unobserve(image); nearby?.observe(image); });
});
